import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CompanyForm from './components/CompanyForm';
import ResultsDisplay from './components/ResultsDisplay';
import StreamingResults from './components/StreamingResults';
import LoadingSpinner from './components/LoadingSpinner';
import ModelConfig from './components/ModelConfig';
import SearchPromptsConfig from './components/SearchPromptsConfig';
import Login from './components/Login';
import ERALogo from './components/ERALogo';
import { AlertCircle, Settings, Globe, LogOut, User } from 'lucide-react';
import './styles/eraTheme.css';

import { API_URL } from './config/api';
const API_KEY = process.env.REACT_APP_API_KEY || '';

function App() {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [showPromptsConfig, setShowPromptsConfig] = useState(false);
  // Streaming is always enabled
  const useStreaming = true;
  const [streamingData, setStreamingData] = useState(null);
  
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  
  // Check for existing session on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
      setIsAuthenticated(true);
      
      // Set default axios header
      axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
    }
  }, []);
  
  const handleLogin = (accessToken, userData) => {
    setToken(accessToken);
    setUser(userData);
    setIsAuthenticated(true);
    
    // Set default axios header
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
  };
  
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setResults(null);
    setError(null);
    
    // Remove default axios header
    delete axios.defaults.headers.common['Authorization'];
  };

  const handleSubmit = async (companyData) => {
    setError(null);
    setResults(null);
    setStreamingData(null);

    if (useStreaming) {
      // Use WebSocket streaming
      setStreamingData(companyData);
    } else {
      // Use traditional REST API
      setLoading(true);
      try {
        const response = await axios.post(
          `${API_URL}/api/v1/categorise-multi`,
          companyData,
          {
            headers: {
              'X-API-Key': API_KEY,
              'Content-Type': 'application/json',
              ...(token && { 'Authorization': `Bearer ${token}` })
            }
          }
        );
        setResults(response.data);
      } catch (err) {
        setError(
          err.response?.data?.detail || 
          err.response?.data?.error ||
          'Failed to categorize company. Please try again.'
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const handleStreamingComplete = () => {
    setStreamingData(null);
  };

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100">
      <div className="container mx-auto px-4 py-8">
        <header className="text-center mb-10 relative">
          <div className="absolute left-0 top-0 flex items-center gap-2">
            <div className="flex items-center bg-white px-4 py-2 rounded-lg shadow">
              <User size={20} className="mr-2 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">{user?.username || 'User'}</span>
            </div>
          </div>
          <div className="absolute right-0 top-0 flex gap-2">
            <button
              onClick={() => setShowPromptsConfig(true)}
              className="p-3 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
              title="Search Prompts Configuration"
            >
              <Globe size={24} />
            </button>
            <button
              onClick={() => setShowConfig(true)}
              className="p-3 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
              title="Model Configuration"
            >
              <Settings size={24} />
            </button>
            <button
              onClick={handleLogout}
              className="p-3 bg-red-100 hover:bg-red-200 rounded-lg transition-colors"
              title="Logout"
            >
              <LogOut size={24} className="text-red-600" />
            </button>
          </div>
          <div className="era-logo-container mb-4">
            <ERALogo />
          </div>
          <h1 className="text-4xl font-bold era-heading mb-2">
            Universal Standard Classification
          </h1>
          <p className="text-lg text-blue-700 mb-1">
            AI-Powered Company Categorization System
          </p>
          <p className="text-sm era-tagline">
            Value through Insight™
          </p>
        </header>

        <div className="max-w-4xl mx-auto">
          <CompanyForm onSubmit={handleSubmit} disabled={loading || streamingData} />

          {error && (
            <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
              <AlertCircle className="text-red-500 mr-3" size={20} />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {loading && <LoadingSpinner />}

          {results && !loading && <ResultsDisplay results={results} />}
          
          {streamingData && (
            <StreamingResults 
              companyData={streamingData} 
              onClose={handleStreamingComplete}
              token={token}
            />
          )}
        </div>
      </div>

      {showConfig && (
        <ModelConfig onClose={() => setShowConfig(false)} />
      )}
      
      {showPromptsConfig && (
        <SearchPromptsConfig onClose={() => setShowPromptsConfig(false)} />
      )}
    </div>
  );
}

export default App;